import {ProductDataStore} from "../../stores/productDataStore";
import {ProductDataService} from "../../services/productDataService";
import {AppStateService} from "../../services/appStateService";
import {UIService} from "../UIService";
import {EventService} from "../../services/eventService";
import {CustomDimensionColorRuleService} from "../../services/customDimentionColorRuleService";
import {PriceStore} from "../../stores/priceStore";
import {CustomerTypeService} from "../../services/customerTypeService";
import {PriceService} from "../../services/priceService";
import {ErrorReportingService} from "../../services/errorReportingService";
import ceil from "lodash/ceil";

export class ColorService {
    private errorReportingService: ErrorReportingService;
    private productDataStore: ProductDataStore;
    private productDataService: ProductDataService;
    private appStateService: AppStateService;
    private uiService: UIService;
    private eventService: EventService;
    private customDimensionColorRuleService: CustomDimensionColorRuleService;
    private priceStore: PriceStore;
    private customerTypeService: CustomerTypeService;
    private priceService: PriceService;
    public selectedOutsidePalette: any;
    public selectedInsidePalette: any;
    public selectedCombination: any;
    public canChangeColor = true;

    constructor() {
        this.productDataStore = new ProductDataStore();
        this.productDataService = new ProductDataService();
        this.appStateService = new AppStateService();
        this.uiService = new UIService();
        this.eventService = new EventService();
        this.customDimensionColorRuleService = new CustomDimensionColorRuleService();
        this.priceStore = new PriceStore();
        this.priceService = new PriceService();
        this.customerTypeService = new CustomerTypeService();
        this.errorReportingService = new ErrorReportingService();
        this.canChangeColor = this.canChangeColorOnProduct();

        if (this.canChangeColor) {
            this.setInitialCombinationAndPalette();
        }
    }

    public resetSelectedOptions() {
        let innerColorId = this.productDataStore.getInitialOptionByName('inner').id;
        let outerColorId = this.productDataStore.getInitialOptionByName('outer').id;
        let sealId = this.productDataStore.getInitialOptionByName('seal').id;

        this.productDataService.updateSelectedOptionByName('inner', innerColorId);
        this.productDataService.updateSelectedOptionByName('outer', outerColorId);
        this.productDataService.updateSelectedOptionByName('seal', sealId, true);

        this.setInitialCombinationAndPalette();
    }

    getOutsidePalettes() {
        const customCombinations = this.productDataStore.getAvailableOptionsByName('customCombination');
        const colorPalettes = this.productDataStore.getAvailableOptionsByName('colorPalettes');
        let outsidePalettes = [];

        customCombinations.forEach(combination => {
            let colorPalette = colorPalettes.filter(palette => palette.id === combination.relationships.paletteOuter.data.id)[0];
            if (colorPalette && outsidePalettes.indexOf(colorPalette) === -1) {
                outsidePalettes.push(colorPalette);
            }
        });

        return outsidePalettes;
    }

    getInsidePalettes() {
        let customCombinations = this.productDataStore.getAvailableOptionsByName('customCombination');
        customCombinations = customCombinations.filter(combination => combination.id === this.selectedCombination.id || this.selectedOutsidePalette.id === combination.relationships.paletteOuter.data.id);
        const colorPalettes = this.productDataStore.getAvailableOptionsByName('colorPalettes');
        let insidePalettes = [];

        customCombinations.forEach(combination => {
            let colorPalette = colorPalettes.filter(color => color.id === combination.relationships.paletteInner.data.id)[0];
            if (colorPalette && insidePalettes.indexOf(colorPalette) === -1) {
                insidePalettes.push(colorPalette);
            }
        });

        return insidePalettes;
    }

    getColorsInPalette(side = 'outer', paletteId = null, searchKeyword = null) {
        if (paletteId === null) {
            if (side === 'outer') {
                paletteId = this.selectedOutsidePalette.id;
            } else {
                paletteId = this.selectedInsidePalette.id;
            }
        }

        let colorsInPalette = this.productDataStore.getAvailableOptionsByName('color');
        return colorsInPalette.filter(color => this.filterColorInPalette(color, paletteId, searchKeyword));
    }

    filterColorInPalette(color, paletteId, searchKeyword) {
        if (color.attributes.paletteIds.indexOf(paletteId) === -1) {
            return false;
        }

        if (this.hideColorOnDimensionColorRule(color)) {
            return false;
        }

        if (searchKeyword !== null && searchKeyword !== '') {
            if (color.attributes.isRal) {
                return (color.attributes.ral.toString().toLowerCase().includes(searchKeyword.toLowerCase()));
            } else {
                return (color.attributes.name.toString().toLowerCase().includes(searchKeyword.toLowerCase()));
            }
        } else if (color.attributes.isNcs) {
            return false;
        }

        return true;
    }

    validateDimensionColorRule() {
        const productId = this.productDataStore.getProductId();
        const width = this.productDataStore.getAttributeByName('width');
        const height = this.productDataStore.getAttributeByName('height');
        const outerColor = this.productDataStore.getSelectedOptionByName('outer');

        this.appStateService.setAppSettingTemplateFlag(
            'colors',
            'dimensionColorRuleNotice',
            !!this.customDimensionColorRuleService.checkProductLimitation(productId, width, height, outerColor)
        );

        this.uiService.refreshElementsInModule('colors');
    }

    hideColorOnDimensionColorRule(colorOption): boolean {
        const productId = this.productDataStore.getProductId();
        const width = this.productDataStore.getAttributeByName('width');
        const height = this.productDataStore.getAttributeByName('height');

        return this.customDimensionColorRuleService.hideColorOnDimensionOverLimit(productId, width, height, colorOption);
    }

    isPaletteNcs(palette) {
        return palette.name.toLowerCase().includes('ncs');
    }

    shouldDefaultColorsBeDisplayed() {
        return this.productDataStore.getProductInfoAttributeByName('showDefaultColors');
    }

    // Method checks if chosen colors matches currently selected combinations. If not, looking for new combinations.
    // Returns true if colors matches current combination or false if not.
    // If false, inside color should be changed according to combination.
    matchCombinationWithSelectedOutsideColor(): boolean {
        if (this.matchCurrentCombination()) {
            return true;
        }

        if (this.matchCombinationWithOneSpecificColor()) {
            return false;
        }

        if (this.matchCombinationBasedOnInnerOuterPalette()) {
            return false;
        }

        return !this.matchCombinationBasedOnOuterPalette();
    }

    private matchCurrentCombination() {
        return this.checkIfColorsMatchesPalette() && this.checkPossibleColorsInCombination();
    }

    private matchCombinationWithOneSpecificColor() {
        const customCombinations = this.productDataStore.getAvailableOptionsByName('customCombination');
        const innerColor = this.productDataStore.getSelectedOption('inner', 'color');
        const outerColor = this.productDataStore.getSelectedOption('outer', 'color');

        let matchedCombination = customCombinations.find(combination => (
            ((combination.relationships.paletteInner.data.colorId === innerColor.id && combination.relationships.paletteOuter.data.colorId === -1) ||
                (combination.relationships.paletteInner.data.colorId === -1 && combination.relationships.paletteOuter.data.colorId === outerColor.id)) &&
            this.checkIfColorsMatchesPalette(combination)
        ));

        if (matchedCombination) {
            this.selectedCombination = matchedCombination;
            this.productDataService.updateSelectedOptionByName('customCombination', matchedCombination.id);
            return true;
        }

        return false;
    }

    private matchCombinationBasedOnInnerOuterPalette() {
        const customCombinations = this.productDataStore.getAvailableOptionsByName('customCombination');
        const innerColor = this.productDataStore.getSelectedOption('inner', 'color');
        const outerColor = this.productDataStore.getSelectedOption('outer', 'color');

        let matchedCombination = customCombinations.find(combination => (
            innerColor.attributes.paletteIds.indexOf(combination.relationships.paletteInner.data.id) > -1 &&
            outerColor.attributes.paletteIds.indexOf(combination.relationships.paletteOuter.data.id) > -1
        ));

        if (matchedCombination) {
            this.selectedCombination = matchedCombination;
            this.productDataService.updateSelectedOptionByName('customCombination', matchedCombination.id);
            return true;
        }

        return false;
    }

    private matchCombinationBasedOnOuterPalette() {
        const customCombinations = this.productDataStore.getAvailableOptionsByName('customCombination');
        const customCombinationsToFilter = customCombinations.filter(elem => elem.id !== this.selectedCombination.id);
        const outerColor = this.productDataStore.getSelectedOption('outer', 'color');
        const outerPalettes = outerColor.attributes.paletteIds;

        for (let index = 0; index < outerPalettes.length; index++) {
            this.selectedCombination = customCombinationsToFilter.find(elem => (
                    elem.relationships.paletteOuter.data.id === outerColor.attributes.paletteIds[index]) &&
                (elem.relationships.paletteOuter.data.colorId === outerColor.id ||
                    elem.relationships.paletteOuter.data.colorId === -1)
            );
            if (this.selectedCombination) {
                break;
            }
        }

        if (this.selectedCombination) {
            this.productDataService.updateSelectedOptionByName('customCombination', this.selectedCombination.id);
            return true;
        }

        return false;
    }

    private checkIfColorsMatchesPalette(combination = null) {
        const innerColor = this.productDataStore.getSelectedOption('inner', 'color');
        const outerColor = this.productDataStore.getSelectedOption('outer', 'color');
        combination = (combination === null) ? this.selectedCombination : combination;

        return (
            innerColor.attributes.paletteIds.indexOf(combination.relationships.paletteInner.data.id) > -1 &&
            outerColor.attributes.paletteIds.indexOf(combination.relationships.paletteOuter.data.id) > -1
        );
    }

    private checkPossibleColorsInCombination() {
        const innerColor = this.productDataStore.getSelectedOption('inner', 'color');
        const outerColor = this.productDataStore.getSelectedOption('outer', 'color');
        return (
            (this.selectedCombination.relationships.paletteOuter.data.colorId === -1 && this.selectedCombination.relationships.paletteInner.data.colorId === -1) ||
            (this.selectedCombination.relationships.paletteOuter.data.colorId === outerColor.id && this.selectedCombination.relationships.paletteInner.data.colorId === -1) ||
            (this.selectedCombination.relationships.paletteOuter.data.colorId === -1 && this.selectedCombination.relationships.paletteInner.data.colorId === innerColor.id)
        );
    }

    private setInitialCombinationAndPalette() {
        const customCombinations = this.productDataStore.getAvailableOptionsByName('customCombination');
        const colorPalettes = this.productDataStore.getAvailableOptionsByName('colorPalettes');
        this.selectedCombination = this.productDataStore.getSelectedOption('customCombination', 'customCombination');

        if (this.selectedCombination === null && customCombinations.length > 0) {
            this.selectedCombination = customCombinations[0];
            this.productDataService.updateSelectedOptionByName('customCombination', this.selectedCombination.id);
        }

        this.selectedOutsidePalette = colorPalettes.find(colorPalette => colorPalette.id === this.selectedCombination.relationships.paletteOuter.data.id);
        this.selectedInsidePalette = colorPalettes.find(colorPalette => colorPalette.id === this.selectedCombination.relationships.paletteInner.data.id);
    }

    public calculateColorsPrice(): {inside: number, outside: number} {
        const selectedOutsideColor = this.productDataStore.getSelectedOption('outer', 'color');
        const selectedInsideColor = this.productDataStore.getSelectedOption('inner', 'color');
        const defaultOutsideColorId = this.productDataStore.getAttributeByName('defaultOuterColorId');
        const defaultInsideColorId = this.productDataStore.getAttributeByName('defaultInnerColorId');
        let prices = {
            'outside': 0,
            'inside': 0,
        };

        if (selectedInsideColor.id === defaultInsideColorId && selectedOutsideColor.id === defaultOutsideColorId) {
            return prices;
        }

        if (this.productDataService.isEntranceDoor()) {
            const standardDoorColorsIds = this.productDataStore.getAvailableOptionsByName('standardColors').map(color => color.id);
            const fieldsCount = this.productDataStore.getAttributeByName('standardFields');
            const entranceDoorColorPrices = this.productDataStore.getAttributeByName('entranceDoorColorPricesInfo');

            let colorIsDoorStandardColor = false;
            if (standardDoorColorsIds.indexOf(selectedOutsideColor.id) > -1) {
                colorIsDoorStandardColor = true;
            }

            if (selectedInsideColor.id === selectedOutsideColor.id && colorIsDoorStandardColor) {
                prices.inside = 0;
                prices.outside = ceil(this.calculateDiscountedPrice(entranceDoorColorPrices[fieldsCount]['standardColor']));
                return prices;
            }

            if (selectedInsideColor.id === selectedOutsideColor.id && !colorIsDoorStandardColor) {
                prices.inside = 0;
                prices.outside = ceil(this.calculateDiscountedPrice(entranceDoorColorPrices[fieldsCount]['nonStandardColor']));
                return prices;
            }

            prices.inside = ceil(this.calculateDiscountedPrice(entranceDoorColorPrices[fieldsCount]['colorTwoSides'] / 2));
            prices.outside = ceil(this.calculateDiscountedPrice(entranceDoorColorPrices[fieldsCount]['colorTwoSides'] / 2));
            return prices;
        }

        const selectedCustomCombination = this.productDataStore.getSelectedOption('customCombination', 'customCombination');
        const selectedPredefinedCombination = this.productDataStore.getSelectedOption('predefinedCombination', 'predefinedCombination');
        let price: number;

        if (selectedPredefinedCombination) {
            const percentage = selectedPredefinedCombination.attributes.precentageSurchargeSingleColor + selectedPredefinedCombination.attributes.precentageSurchargeBiColor;
            price = this.calculatePriceBasedOnSurcharge(percentage);
        } else {
            if (selectedInsideColor.id === selectedOutsideColor.id || selectedInsideColor.id === defaultInsideColorId || selectedOutsideColor.id === defaultOutsideColorId) {
                price = this.calculatePriceBasedOnSurcharge(selectedCustomCombination.attributes.precentageSurchargeSingleColor);
            } else {
                price = this.calculatePriceBasedOnSurcharge(selectedCustomCombination.attributes.precentageSurchargeBiColor);
            }
        }

        if (selectedInsideColor.id === defaultInsideColorId) {
            prices.inside = 0;
            prices.outside = ceil(price);
        } else if (selectedOutsideColor.id === defaultOutsideColorId) {
            prices.inside = ceil(price);
            prices.outside = 0;
        } else {
            prices.inside = ceil(price / 2);
            prices.outside = ceil(price / 2);
        }

        return prices;
    }

    private calculatePriceBasedOnSurcharge(surcharge)
    {
        let basePrice = 0;

        try {
            basePrice = this.priceStore.get()[0].basePrice;
        } catch (exception) {
            this.errorReportingService.display(exception.message);
        }
        basePrice = (this.customerTypeService.isB2bCustomer()) ? this.priceService.calculateNetPrice(basePrice) : basePrice;

        const price = basePrice * surcharge;

        return this.calculateDiscountedPrice(price);
    }

    private calculateDiscountedPrice(price)
    {
        let lineDiscountValue = 0;

        try {
            lineDiscountValue = this.priceStore.get()[0].lineDiscountValue;
        } catch (exception) {
            this.errorReportingService.display(exception.message);
        }
        const discount = price * (lineDiscountValue / 100);

        return price - discount;
    }

    private canChangeColorOnProduct(): boolean
    {
        return (this.productDataStore.getAvailableOptionsByName('customCombination').length > 0);
    }
}
